import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowUpRight, ShieldCheck, Radio, Stamp, Cpu, Landmark } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1757466762489-52fea38071ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBkYXRhJTIwbmV0d29yayUyMGRhcmt8ZW58MHx8fHwxNzg4NDUyODI3fDA&ixlib=rb-4.1.0&q=85";
const CARD_IMG = "https://images.unsplash.com/photo-1639133694967-640f255f10fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwY3JlZGl0JTIwY2FyZCUyMGRhcmt8ZW58MHx8fHwxNzg4NDUyODMyfDA&ixlib=rb-4.1.0&q=85";
const STAMP_IMG = "https://images.unsplash.com/photo-1761437856299-af640f6e75ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGR8ZW58MHx8fHwxNzg4NDUyODQxfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rates, setRates] = useState({});
  const [concept, setConcept] = useState(null);
  const [version, setVersion] = useState(null);

  useEffect(() => {
    api.get("/exchange-rates").then((r) => setRates(r.data.rates || {})).catch(() => {});
    api.get("/concept").then((r) => setConcept(r.data)).catch(() => {});
    api.get("/version").then((r) => setVersion(r.data)).catch(() => {});
    const t = setInterval(() => {
      api.get("/exchange-rates").then((r) => setRates(r.data.rates || {})).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const rateArr = Object.values(rates);

  return (
    <div className="min-h-screen text-white">
      {/* Nav */}
      <nav className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#7a5f16] grid place-items-center font-display font-black text-black">★</div>
            <div className="leading-none">
              <div className="font-display text-lg font-bold tracking-tight">BlackStar</div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-white/60">VIRTUAL MINT</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#concept" className="link-underline">Doctrine</a>
            <a href="#rails" className="link-underline">Rails</a>
            <a href="#stamps" className="link-underline">Tax Stamps</a>
            <a href="#atm" className="link-underline">ATM IP-Map</a>
            <Link to="/atm-locator" data-testid="nav-locator-link" className="link-underline">Find ATM</Link>
            <Link to="/starcoin" data-testid="nav-starcoin-link" className="link-underline">StarCoin</Link>
            <Link to="/status" data-testid="nav-status-link" className="link-underline">Status</Link>
            <Link to="/merchants" data-testid="nav-merchants-link" className="link-underline">Merchants</Link>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <button data-testid="nav-dashboard-btn" onClick={() => nav("/app")} className="btn-primary">
                Enter Vault →
              </button>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="btn-ghost">Sign in</Link>
                <Link to="/register" data-testid="nav-register-link" className="btn-primary">Open Account</Link>
              </>
            )}
          </div>
        </div>
        {/* Ticker */}
        <div className="border-t border-white/5 bg-black/50">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {rateArr.map((r) => (
              <div key={r.code} className="ticker-item" data-testid={`ticker-${r.code}`}>
                <span className="pulse-dot" />
                <span className="text-white/80">{r.code}</span>
                <span className="text-white/50">{r.name}</span>
                <span className="text-[#d4af37]">${r.rate_usd?.toFixed(4)}</span>
                <span className={r.change_pct >= 0 ? "text-[#39ff14]" : "text-[#ff3366]"}>
                  {r.change_pct >= 0 ? "▲" : "▼"} {Math.abs(r.change_pct).toFixed(3)}%
                </span>
                <span className="text-white/30">HYP {r.protected_rate_usd?.toFixed(4)}</span>
              </div>
            ))}
          </Marquee>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-[#050505]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-8">
              <div className="chip gold mb-6" data-testid="hero-chip">◆ v1.4.0 · CREDIT-INITIATED · ISO 20022</div>
              <h1 className="font-display font-black leading-[0.95] tracking-tighter text-white" style={{fontSize:'clamp(3rem, 7vw, 6rem)'}}>
                The Mint that <span className="italic text-[#d4af37]">eliminates</span><br/>
                inflation, by design.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                BlackStar Virtual Mint is cutting-edge neo-banking where every unit of cash is minted at the ledger, backed by digital tax-credit stamps, and dispatched as an ATM-redeemable money order through IP-mapped source-code routing.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button data-testid="hero-cta-open" onClick={() => nav("/register")} className="btn-primary inline-flex items-center gap-2">
                  Claim your Vault <ArrowUpRight size={18}/>
                </button>
                <a href="#concept" data-testid="hero-cta-doctrine" className="btn-ghost">Read the Doctrine</a>
              </div>
              <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  ["S00→S13", "State-Machine Ledger"],
                  ["ISO 20022", "pacs · camt · pain"],
                  ["PCI-DSS", "Card-Vault Encrypted"],
                  ["LEI 17442", "Regulator Chain"],
                ].map(([k,v]) => (
                  <div key={k} className="panel p-4">
                    <div className="font-mono text-[#d4af37] text-sm">{k}</div>
                    <div className="text-xs text-white/60 mt-1">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="card-3d p-6 aspect-[1.6/1] flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <div className="font-display text-2xl">★</div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#d4af37]">BLACKSTAR</div>
                </div>
                <div>
                  <div className="font-mono text-xl tracking-widest text-white/90">4532 •••• •••• 0842</div>
                  <div className="flex justify-between mt-3 text-xs text-white/60">
                    <span>M. AGADA</span>
                    <span>12/30</span>
                    <span className="text-[#d4af37]">AGADA TIER</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 panel p-4">
                <div className="label mb-2">Credit-Initiated Grant</div>
                <div className="font-display text-3xl text-[#39ff14]">$250,000.00</div>
                <div className="text-xs text-white/50 mt-1 font-mono">Seeded by database at S06</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Doctrine */}
      <section id="concept" className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label text-[#d4af37]">01 · Doctrine</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">Virtual Finance, to the letter.</h2>
          </div>
          <div className="hidden md:block text-white/50 max-w-md text-sm">A protocol built on ISO 4217, ISO 20022, ISO 9362, LEI (17442), PCI-DSS and ISO/IEC 27001 — enforced by a deterministic state machine.</div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {(concept?.sections || []).map((s, i) => (
            <div key={s.heading} className="panel p-8" data-testid={`doctrine-card-${i}`}>
              <div className="chip cyan mb-4">§ {String(i+1).padStart(2,"0")}</div>
              <h3 className="font-display text-2xl mb-3">{s.heading}</h3>
              <p className="text-white/70 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rails */}
      <section id="rails" className="max-w-7xl mx-auto px-6 py-24">
        <div className="label text-[#d4af37]">02 · Rails</div>
        <h2 className="font-display text-4xl md:text-5xl mt-3 mb-12">Every rail. One mint.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: Landmark, title:"Central Bank Dispatch", body:"FED, ECB, BOE, BOJ, PBOC, SNB routing tables baked in."},
            {icon: Radio,    title:"UK Faster Payments",   body:"Sort-code, CoP, single-payment & aggregate FPS limits enforced."},
            {icon: Cpu,      title:"Modem-Hash Finance",   body:"IMEI-bound endpoints route money-orders behind SSL/TLS."},
            {icon: ShieldCheck, title:"PCI-DSS Card Vault", body:"Luhn-checked virtual & thermal RFID cards, hashed CVV."},
            {icon: Stamp,    title:"ISO 20022 Composer",   body:"pacs.008 / pacs.002 / camt.053 generated live at S09."},
            {icon: ArrowUpRight, title:"ATM Money Orders", body:"Every send-money is cash-redeemable via IP → sort-code map."},
          ].map((r, i) => (
            <div key={r.title} className="panel p-6" data-testid={`rail-card-${i}`}>
              <r.icon size={22} className="text-[#d4af37]"/>
              <div className="font-display text-xl mt-4">{r.title}</div>
              <div className="text-sm text-white/60 mt-2">{r.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Stamps */}
      <section id="stamps" className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        <img src={STAMP_IMG} alt="stamps" className="rounded-2xl border border-white/10"/>
        <div>
          <div className="label text-[#d4af37]">03 · Tax-Credit Stamps</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 mb-6">Sovereign-grade receipts, redeemable one-to-one.</h2>
          <p className="text-white/70 leading-relaxed mb-4">Every digital tax-credit stamp is a fungible bearer receipt backed by public defence coverage. Redeem to your vault and the balance credits instantly.</p>
          <ul className="space-y-2 text-white/70 text-sm">
            <li>◆ Denominations $50, $100, £250, €500</li>
            <li>◆ Issued by BlackStar Treasury under ISO 9362</li>
            <li>◆ Redeemable inside the vault, or bearer-transferable</li>
          </ul>
        </div>
      </section>

      {/* ATM IP Map */}
      <section id="atm" className="max-w-7xl mx-auto px-6 py-24">
        <div className="label text-[#d4af37]">04 · ATM IP-Mapping</div>
        <h2 className="font-display text-4xl md:text-5xl mt-3 mb-8">Money orders that any ATM can honour.</h2>
        <div className="panel p-8">
          <div className="font-mono text-sm text-white/70 whitespace-pre-wrap">
{`▸ CLIENT_IP  ────────►  81.144.220.42
▸ RESOLVE    ────────►  country=GB · sort=20-00-00
▸ MAP        ────────►  BIC=BSVMGB2LXXX · Network=FPS
▸ COMPOSE    ────────►  ISO 20022 pacs.008.001.10
▸ DISPATCH   ────────►  S11 Central Bank of England
▸ SETTLE     ────────►  S13 · cash-redeemable at any BLACKSTAR ATM`}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div data-testid="footer-version">
            © 2026 BlackStar Virtual Mint · <span className="font-mono text-[#d4af37]">{version?.version || "v1.4.x"}</span> · LEI 5493001BLACKSTAR000
          </div>
          <div className="font-mono text-xs">
            {version ? `${version.modules.length} modules · ${version.state_machine_states} states · ${version.atm_count} ATMs` : "Regulated · State-Machine Certified"}
          </div>
        </div>
      </footer>
    </div>
  );
}
